/* eslint-disable simple-import-sort/imports */
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchUserLocations } from "../../../../js/features/ManageWorkers/ManageWorkersSlice";
import { FilterContext } from "../../filters/filter-context";
import {
	buildSkillWorkCenterConstraint,
	getAppliedLocationIds,
	getAppliedWorkCenterIds,
	skillMatchesWorkCenters,
} from "../../insight-dashboard/utils/summary-filter-utils";
import {
	fetchskilllevels,
	getWorkerInsightReport,
} from "../../insight-dashboard/slices/worker-insight-report-slice";
import crossSkillingDashboardControllerData from "./use-cross-skilling-dashboard-data";
import { getSkillInsightReport } from "../../insight-dashboard/slices/skill-insight-report-slice";
import {
	Location,
	SkillInsightModel,
	SkillInsightsDetails,
	WorkerInsightModel, columnType,
} from "../../insight-dashboard/slices/insight-report-types";
import { getSkillPriorities } from "../../skills/store/skills-slice";
import useTableHeight from "../../../utils/use-table-height";

const useCrossSkillingDashboardController = () => {
	const {
		workersReportData,
		fetchWorkerInsightReportStatus,
		allLocations,
		allSkillLevels,
		skillsReportData,
		fetchSkillInsightReportStatus,
		allPriorities,
		isSkillPriorityEnabled,
		jobSkillLevelMapping,
	} = crossSkillingDashboardControllerData();

	const { t } = useTranslation();
	const dispatch = useDispatch();

  const {boxRef, height} = useTableHeight();

	const [selectedLocations, setSelectedLocations] = useState<Array<number>>([]);
	const [searchText, setSearchText] = useState("");
	const [filteredWorkerSummariesData, setFilteredWorkerSummariesData] =
		useState(null);
	const [filteredSkillSummariesData, setFilteredSkillSummariesData] =
		useState(null);
	const [selectedKPI, setKpi] = useState<
		| "skillsWithLessThenXWorkersKPI"
		| "interestedWorkersKPI"
		| "workersWithLessThenXSkillsKPI"
		| "workersWithAllSkillsKPI"
		| "overallWorkers"
	>("overallWorkers");
	const [overAllWorkersData, setOverallWorkersData] =
		useState(workersReportData);
	const [overAllSkillsData, setOverallSkillsData] = useState(skillsReportData);

	const [workersKPIColumns, setWorkersKPIColumns] = useState<columnType[]>([]);
	const [skillsKPIColumns, setSkillsKPIColumns] = useState<columnType[]>([]);

	const [kpiCount, setKpiCount] = useState({
		skillsWithLessThenXWorkersKPI: 0,
		interestedWorkersKPI: 0,
		workersWithLessThenXSkillsKPI: 0,
		workersWithAllSkillsKPI: 0,
	});

	useEffect(() => {
		try {
			dispatch(getWorkerInsightReport());
			dispatch(fetchUserLocations());
			dispatch(fetchskilllevels());
			dispatch(getSkillPriorities());
			dispatch(getSkillInsightReport());
		} catch (e) {
			// Do nothing
		}
	}, [dispatch]);

	useEffect(() => {
		const locations = allLocations.map((item: Location) => item.id);
		setSelectedLocations(locations);
	}, [allLocations]);

	// When rendered inside a FilterProvider (Summary tab), the Location and
	// Work Center chips drive the filtering: the Location chips are translated
	// into the selectedLocations array the KPI computations already consume
	// (no chips = all locations), and the Work Center chips produce an id set
	// applied to the worker rows below.
	const filterContext = useContext(FilterContext);
	const useSharedFilters = !!filterContext;
	const appliedFilters = filterContext?.appliedFilters;

	const appliedLocationIds = useMemo(
		() => getAppliedLocationIds(appliedFilters ?? []),
		[appliedFilters]
	);

	const appliedWorkCenterIds = useMemo(
		() => getAppliedWorkCenterIds(appliedFilters ?? []),
		[appliedFilters]
	);

	// Work Center chips resolved to skills via the jobs mapping (see
	// summary-filter-utils). Null = no constraint.
	const dataRelations = filterContext?.dataRelations;
	const appliedSkillConstraint = useMemo(
		() => buildSkillWorkCenterConstraint(appliedWorkCenterIds, dataRelations),
		[appliedWorkCenterIds, dataRelations]
	);

	useEffect(() => {
		if (!useSharedFilters) return;
		const allIds = allLocations.map((item: Location) => item.id);
		setSelectedLocations(appliedLocationIds ?? allIds);
	}, [useSharedFilters, appliedLocationIds, allLocations]);

	useEffect(() => {
		setKpi("overallWorkers");

		if (selectedLocations.length === 0) {
			setKpiCount({
				skillsWithLessThenXWorkersKPI: 0,
				interestedWorkersKPI: 0,
				workersWithLessThenXSkillsKPI: 0,
				workersWithAllSkillsKPI: 0,
			});
		}
	}, [selectedLocations]);

	useEffect(() => {
		let workersColumns = [
			{
				checked: true,
				field: "workerName",
				headerName: t("cross-skilling-dashboard_worker-name"),
			},
			{
				checked: true,
				field: "workerLocation",
				headerName: t("cross-skilling-dashboard_worker-location"),
			},
			{
				checked: true,
				field: "workcenterName",
				headerName: t("cross-skilling-dashboard_workcenter"),
			},
			{
				checked: false,
				field: "primaryJobName",
				headerName: t("cross-skilling-dashboard_primary-job"),
			},
			{
				checked: false,
				field: "joiningDate",
				headerName: t("cross-skilling-dashboard_joining-date"),
			},
			{
				checked: true,
				field: "requiredSkillsCount",
				headerName: t("cross-skilling-dashboard_required-skills"),
			},
			{
				checked: true,
				field: "totalSkills",
				headerName: t("cross-skilling-dashboard_total-skills"),
			},
			{
				checked: true,
				field: "workerSkillsDemostratedCount",
				headerName: t("cross-skilling-dashboard_skills-demonstrated"),
			},
			{
				checked: true,
				field: "workerSkillsGapCount",
				headerName: t("cross-skilling-dashboard_skills-gap"),
			},
			{
				checked: true,
				field: "workerOtherSkillsCount",
				headerName: t("cross-skilling-dashboard_other-skills"),
			},
			{
				checked: true,
				field: "skillScore",
				headerName: t("cross-skilling-dashboard_total-skill-score"),
			},
			{
				checked: true,
				field: "interestedSkillsCount",
				headerName: t("cross-skilling-dashboard_interested-skills"),
			},
			{
				checked: true,
				field: "workerBackupCount",
				headerName: t("cross-skilling-dashboard_worker-backup"),
			},
		];

		const skillsColumns = [
			{
				checked: true,
				field: "skillName",
				headerName: t("cross-skilling-dashboard_skill-name"),
			},
			{
				checked: true,
				field: "skillPriority",
				headerName: t("cross-skilling-dashboard_skill-priority"),
			},
			{
				checked: true,
				field: "interestedWorkersCount",
				headerName: t("cross-skilling-dashboard_interested-workers"),
			},
			{
				checked: true,
				field: "totalWorkers",
				headerName: t("cross-skilling-dashboard_workers-with-skill"),
			},
		];
		if (
			selectedKPI === "skillsWithLessThenXWorkersKPI" ||
			selectedKPI === "interestedWorkersKPI"
		) {
			const skillColumns = isSkillPriorityEnabled
				? skillsColumns
				: skillsColumns.filter((item) => item.field !== "skillPriority");
			// Keep the checked status based on previous state
			setSkillsKPIColumns(prevState => {
				return skillColumns.map((column) => {
					const prevColumn = prevState.find((prevColumn) => prevColumn.field === column.field);
					return prevColumn ? { ...column, checked: prevColumn.checked } : column;
				});
			});
		} else {
			if (!isSkillPriorityEnabled) {
				workersColumns = workersColumns.filter((item) => item.field !== "skillScore");
            }
            if (jobSkillLevelMapping) {
				workersColumns = workersColumns.filter((item) => item.field !== "totalSkills");
				workersColumns = workersColumns.map((item) => {
					if (item.field === "primaryJobName") {
						return {
							...item,
							checked: true,
						}
					} else {
						return item;
					}
				});
            } else {
				workersColumns = workersColumns.filter((item) =>
					!["requiredSkillsCount","workerSkillsDemostratedCount", "workerSkillsGapCount","workerOtherSkillsCount"].includes(item.field));
			}
			// Keep the checked status based on previous state
			setWorkersKPIColumns(prevState => {
				return workersColumns.map((column) => {
					const prevColumn = prevState.find((prevColumn) => prevColumn.field === column.field);
					return prevColumn ? { ...column, checked: prevColumn.checked } : column;
				});
			});
		}
	}, [selectedKPI, isSkillPriorityEnabled, jobSkillLevelMapping, t]);

	const [workersWithLessThenXSkills, setworkersWithLessThenXSkills] = useState<
		WorkerInsightModel[]
	>([]);

	const [workersWithAllSkills, setWorkersWithAllSkills] =
		useState(workersReportData);

	const [skillsWithLessThanXWorkers, setskillsWithLessThanXWorkers] = useState<
		SkillInsightsDetails[]
	>([]);

	useEffect(() => {
		const workersWithLessThenXSkillsData: WorkerInsightModel[] = [];

		//Filter by locations
		if (selectedLocations.length === 0) {
			setworkersWithLessThenXSkills([]);
			setOverallWorkersData(workersReportData);
			setKpiCount(prev => ({
				...prev,
				workersWithLessThenXSkillsKPI: 0
			}));
			return;
		}
		let filteredData: WorkerInsightModel[] = [];

		if (selectedLocations?.length === allLocations.length) {
			filteredData = [...workersReportData];
		} else {
			workersReportData
				.filter((worker) => selectedLocations.includes(worker.locationId))
				.forEach((item) => {
					const workerData = { ...item };
					filteredData.push(workerData);
				});
		}

		// Work Center chips (Summary tab shared filters) apply to worker rows
		// only — skill insight data has no work-center dimension.
		if (appliedWorkCenterIds) {
			filteredData = filteredData.filter((worker) =>
				appliedWorkCenterIds.has(worker.workCenterId)
			);
		}

		setOverallWorkersData(filteredData);

		filteredData.forEach((item) => {
			let totalSkillsCount = 0;
			item.workerSkillCountByLevel.forEach((skill) => {
				const lev = allSkillLevels.find((level) => level.level === skill.level);
				if (lev && lev.isQualifiedAsSkilled) {
					totalSkillsCount += skill.count;
				}
			});

			if (totalSkillsCount < 3) {
				workersWithLessThenXSkillsData.push(item);
			}
		});

		if (workersWithLessThenXSkillsData.length === 0) {
			const sortedWorkerData: WorkerInsightModel[] = [...filteredData].sort(
				(a, b) => {
					const sumA = a.workerSkillCountByLevel.reduce(
						(acc: any, obj: any) => acc + obj.count,
						0
					);
					const sumB = b.workerSkillCountByLevel.reduce(
						(acc: any, obj: any) => acc + obj.count,
						0
					);
					return sumB - sumA;
				}
			);
			setworkersWithLessThenXSkills(sortedWorkerData);
			setKpiCount(prev => ({
				...prev,
				workersWithLessThenXSkillsKPI: 0
			}));
		} else {
			setworkersWithLessThenXSkills(workersWithLessThenXSkillsData);
			setKpiCount(prev => ({
				...prev,
				workersWithLessThenXSkillsKPI: workersWithLessThenXSkillsData.length
			}));
		}
	}, [
		workersReportData,
		selectedLocations,
		allLocations,
		allSkillLevels,
		appliedWorkCenterIds,
	]);

	useEffect(() => {
		try {
			const skillsWithLessThanXWorkersData: SkillInsightsDetails[] = [];

			//Filter by location
			const selectedLocationWithIds: Location[] = [];

			selectedLocations?.map((location: number) => {
				const data = allLocations.find((x: Location) => x.id === location);
				selectedLocationWithIds.push({
					id: data?.id,
					name: data?.name,
					isActive: data?.isActive,
				});
			});

			if (selectedLocationWithIds.length === 0) {
				setskillsWithLessThanXWorkers([]);
				setOverallSkillsData(skillsReportData);
				setKpiCount(prev => ({
					...prev,
					skillsWithLessThenXWorkersKPI: 0,
					interestedWorkersKPI: 0
				}));
				return;
			}

			let filtereSkillsdDataByLoc: SkillInsightsDetails[] = [];

			if (selectedLocationWithIds?.length !== allLocations.length) {
				skillsReportData.forEach((item: SkillInsightsDetails) => {
					if (
						selectedLocationWithIds.some((location) =>
							item.locationIds.includes(location.id)
						)
					) {
						const skillData = { ...item };
						filtereSkillsdDataByLoc.push(skillData);
					}
				});
			} else {
				filtereSkillsdDataByLoc = [...skillsReportData];
			}

			// Work Center chips, resolved to skills via jobs. Skills without
			// any job association pass through (location-only check).
			if (appliedSkillConstraint) {
				filtereSkillsdDataByLoc = filtereSkillsdDataByLoc.filter((item) =>
					skillMatchesWorkCenters(item.skillId, appliedSkillConstraint)
				);
			}

			setOverallSkillsData(filtereSkillsdDataByLoc);
			let totalInterestedWorkersFinalCount = 0;
			filtereSkillsdDataByLoc.forEach((item) => {
				let totalWorkersCount = 0;
				item.workersSkillLevel.forEach((skill) => {
					if (selectedLocations.length) {
						// If filter is applied
						if (
							selectedLocations.includes(skill.locationId)
						) {
							skill.workerCount.forEach((level) => {
								const selectedSkillLevel = allSkillLevels.find(skillLevel => skillLevel.level === level.level);
								if (selectedSkillLevel && selectedSkillLevel.isQualifiedAsSkilled) {
									totalWorkersCount += level.count;
								}
							});
						}
					} else {
						skill.workerCount.forEach((level) => {
							const selectedSkillLevel = allSkillLevels.find(skillLevel => skillLevel.level === level.level);
							if (selectedSkillLevel && selectedSkillLevel.isQualifiedAsSkilled) {
								totalWorkersCount += level.count;
							}
						});
					}
				})
				if (totalWorkersCount < 3) {
					skillsWithLessThanXWorkersData.push(item);
				}

				const interestedWorkersObject = item.interestedWorkersCount
					.filter((x) => selectedLocations.includes(x.locationId)) // Filter by selected locations
					.flatMap((x) => x.count);
				const totalInterestedWorkersCount = interestedWorkersObject.reduce(
					(sum, skill) => sum + skill,
					0
				);
				totalInterestedWorkersFinalCount += totalInterestedWorkersCount;
			});

			if (skillsWithLessThanXWorkersData.length === 0) {
				const sortedSkillsData: SkillInsightsDetails[] = [
					...filtereSkillsdDataByLoc,
				].sort((a, b) => {
					const sumA = a.workersSkillLevel.reduce(
						(acc: any, obj: any) => acc + obj.count,
						0
					);
					const sumB = b.workersSkillLevel.reduce(
						(acc: any, obj: any) => acc + obj.count,
						0
					);
					return sumA - sumB;
				});
				setskillsWithLessThanXWorkers(sortedSkillsData);
				setKpiCount(prev => ({
					...prev,
					skillsWithLessThenXWorkersKPI: 0,
					interestedWorkersKPI: totalInterestedWorkersFinalCount
				}));
			} else {
				setskillsWithLessThanXWorkers(skillsWithLessThanXWorkersData);
				setKpiCount(prev => ({
					...prev,
					skillsWithLessThenXWorkersKPI: skillsWithLessThanXWorkersData.length,
					interestedWorkersKPI: totalInterestedWorkersFinalCount
				}));
			}

		} catch (error) {
			console.log("Error in insight dashboard",error);
		}
	}, [
		skillsReportData,
		selectedLocations,
		allLocations,
		allSkillLevels,
		appliedSkillConstraint,
	]);

	const priorities = allPriorities.reduce((acc: any, obj: any) => {
		acc[obj.id] = obj;
		return acc;
	}, {});

	return [
		{
			allLocations,
			selectedLocations,
			allSkillLevels,
			workersReportData,
			fetchWorkerInsightReportStatus,
			searchText,
			filteredWorkerSummariesData,
			skillsReportData,
			filteredSkillSummariesData,
			fetchSkillInsightReportStatus,
			isSkillPriorityEnabled,
			jobSkillLevelMapping,
			skillsWithLessThanXWorkers,
			workersWithLessThenXSkills,
			workersWithAllSkills,
			selectedKPI,
			kpiCount,
			overAllSkillsData,
			overAllWorkersData,
			allPriorities,
			priorities,
			skillsKPIColumns,
			workersKPIColumns,
      height,
      boxRef,
		},
		{
			setSelectedLocations,
			setSearchText,
			dispatch,
			setFilteredWorkerSummariesData,
			setFilteredSkillSummariesData,
			setKpi,
			setSkillsKPIColumns,
			setWorkersKPIColumns,
		},
	];
};

export default useCrossSkillingDashboardController;
